import ICustomForm from "../../ICustomForm";
import m, {Vnode} from "mithril";
import ICustomFormSettings from "../../ICustomFormSettings";
import Design from "../../../ui/design";
import StarsCell from "../../shared/components/FormCell/StarsCell";
import {BaseStars} from "../BaseStars";
import {LabelItem} from "../../../model/items/labelItem";
import {RadioItem} from "../../../model/items/radio";
import Cell from "../../../model/form/cell";

export default class StarsMultiple extends BaseStars implements ICustomForm
{
    private readonly representation: "oneByOne"|"all";

    private currentRow: number = 0;
    private interaction: boolean = false;
    private starsItems: StarsCell[] = [];
    private ratingNames: LabelItem[] = [];

    constructor(settings: ICustomFormSettings) {
        super(settings);

        const labels: LabelItem[] = [];
        const ratingNames: LabelItem[] = [];
        const variableGetters: (() => null|number)[] = [];
        const variableSetters: ((rating: number) => void)[] = [];
        const cells: Cell[] = [];

        // Read rows with stars in
        for (const cell of settings.form.grid.cells) {
            for (const cellItem of cell.cellItems) {
                if (cellItem instanceof LabelItem) {
                    if (cell.column === 0) {
                        ratingNames[cell.row] = cellItem;
                    } else {
                        labels.push(cellItem);
                    }
                } else if (cellItem instanceof RadioItem && !variableGetters[cell.row]) {
                    cells[cell.row] = cell;
                    variableGetters[cell.row] = () => cellItem.getVariable();
                    variableSetters[cell.row] = (rating) => cellItem.setVariable(rating);
                }
            }
        }

        // Create StarsItems
        for (const i in ratingNames) {
            if (cells[i].isVisible()) {
                const starItem = new StarsCell(
                    5,
                    5,
                    variableGetters[i],
                    variableSetters[i],
                    labels
                );
                starItem.setEnabled(cells[i].enabled);
                this.starsItems.push(starItem);
                this.ratingNames.push(ratingNames[i]);
            }
        }

        // choose representation
        this.representation = (window.innerWidth >= Design.BC.md && window.innerHeight > 575) ? "all" : "oneByOne";

        if (this.representation === "oneByOne") {

            // find last answered StarsItem
            for (const i in this.starsItems) {
                if (this.starsItems[i].value) {
                    this.currentRow = Number(i);
                } else {
                    break;
                }
            }
        }
    }

    private existPrevious(): boolean {
        return this.currentRow !== 0;
    }

    protected existNext(): boolean {
        return this.currentRow+1 < this.starsItems.length;
    }

    protected nextSlider(): void {
        this.interaction = true;
        if (this.currentRow < this.starsItems.length-1) {
            this.currentRow++;
        }
    }

    protected previousSlider(): void {
        this.interaction = true;
        if (this.currentRow > 0) {
            this.currentRow--;
        }
    }

    protected viewControls(nextButtonText: string = 'nextButton') {
        if (this.representation === "all") {
            return super.viewControls();
        }

        const form = this.settings.form;
        const questionary = form.questionary;

        const existPrevSlider = this.existPrevious();
        const existNextSlider = this.existNext();

        const visiblePreviousButton = this.settings.app.prevEnabled() || existPrevSlider;
        const visibleLangButton = this.settings.app.langSwitcherEnabled();
        const visibleNextButton = this.settings.app.nextEnabled() || existNextSlider;
        const enableNextButton = this.starsItems[this.currentRow].value;

        return m(".controls", {},
            m(".control-group", {}, [
                visiblePreviousButton ? m("button.btn.large.outline.secondary#previousButton", {
                    onclick: () => {
                        if (existPrevSlider) {
                            this.previousSlider()
                        } else {
                            this.settings.prevForm()
                        }
                    },
                    title: questionary.renderText(questionary.messages.get('backButton')),
                }, [
                    m('i.icon.icon-chevron-left', {}, '')
                ]) : null,

                visibleLangButton ? m("button.btn.large.outline.secondary#languageButton", {
                    onclick: () => this.settings.selectLanguage(),
                    title: questionary.renderText(questionary.messages.get('language'))
                }, [
                    m('i.icon.icon-translate', {}, '')
                ]) : null,

                visibleNextButton ? m("button.btn.large.raised.primary.has-primary-background#nextButton", {
                    onclick: () => {
                        if (existNextSlider) {
                            this.nextSlider()
                        } else {
                            this.settings.nextForm()
                        }
                    },
                    disabled: enableNextButton ? false : 'disabled',
                }, [
                    m('span', {}, questionary.renderText(questionary.messages.get(nextButtonText)) ),
                    m('i.icon.icon-chevron-right'+(enableNextButton?'.text-light':''), {}, '')
                ]) : null,
            ])
        );
    }

    private titleView(label?: LabelItem): Vnode|null {
        if (!label) {
            return null;
        }
        const questionary = label.cell.grid.questionary;
        const text = label.childrenTags.filter(t => t.tag === 'text')[0]?.value;
        return m('.title', {}, m.trust(questionary.renderText(text ? text : '')));
    }


    public view(): Vnode {
        if (this.representation === "all") {
            const items = [];
            for (const i in this.starsItems) {
                items.push(this.titleView(this.ratingNames[i]));
                items.push(m(this.starsItems[i]));
            }

            return this.viewLayout(
                m("form.representation-"+this.representation, {}, items ),
            );
        } else {
            return this.viewLayout(
                m("form.representation-"+this.representation, {}, [
                    this.titleView(this.ratingNames[this.currentRow]),
                    m(this.starsItems[this.currentRow]),
                ]),
            );
        }
    }
}
